import {Component, OnInit} from '@angular/core';
import {FormBuilder, FormGroup, Validators} from '@angular/forms';
import UserModel from '../../services/user.model';
import {ActivatedRoute, Router} from '@angular/router';
import {UserService} from '../../services/user.service';
import { LocationStrategy } from '@angular/common';
import { PreviousRouteService } from 'src/app/services/previousRouteService';
import SessionModel from '../../services/session.model';
import {SessionService} from '../../services/session.service';

/**
 * ResetpasswordComponent
 * 
 * A component that handles password reset functionality for users who have forgotten their password.
 * Users provide their mobile number and create a new password after validation.
 * The component validates the password strength and confirms password match before updating.
 * 
 * Key features:
 * - Mobile number verification
 * - Password validation (6-10 characters)
 * - Confirm password matching
 * - Person type and address validation
 * - Session initialization for guest access
 * - Previous route tracking
 * 
 * @Component({
 *   selector: 'app-resetpassword',
 *   templateUrl: './resetpassword.component.html',
 *   styleUrls: ['./resetpassword.component.scss']
 * })
 * 
 * @example
 * <app-resetpassword></app-resetpassword>
 * 
 * @implements {OnInit}
 */
@Component({
  selector: 'app-resetpassword',
  templateUrl: './resetpassword.component.html',
  styleUrls: ['./resetpassword.component.scss']
})
export class ResetpasswordComponent implements OnInit {
  /** 
   * Form group for password reset form
   * Contains usernumber, password, and confirmpassword controls with validators
   * @type {FormGroup}
   */
  public resetPasswordForm: FormGroup;
  
  /** 
   * Flag indicating if the form has been submitted
   * Used to show validation error messages
   * @type {boolean}
   * @default false
   */
  public submitted = false;
  
  /** 
   * User details model containing username and password fields
   * @type {UserModel}
   */
  public userDetails: UserModel;
  
  /** 
   * Current session model for guest access during password reset
   * @type {SessionModel}
   */
  public sessionModel: SessionModel;

  /**
   * Constructor for ResetpasswordComponent
   * 
   * Initializes all required services and creates the password reset form with validators.
   * Form validation rules:
   * - usernumber: Required
   * - password: Required, 6-10 characters
   * - confirmpassword: Required
   * 
   * @constructor
   * @param {SessionService} sessionService - Service for session management
   * @param {Router} router - Angular router for navigation
   * @param {ActivatedRoute} route - Active route for route parameters
   * @param {UserService} userService - Service for user operations
   * @param {FormBuilder} formBuilder - Angular form builder for creating reactive forms
   * @param {LocationStrategy} locationStrategy - Angular location strategy for URL management
   * @param {PreviousRouteService} previousRouteService - Service for tracking navigation history
   */
  constructor(public sessionService: SessionService, public router: Router, public route: ActivatedRoute, public userService: UserService, public formBuilder: FormBuilder, private locationStrategy: LocationStrategy, private previousRouteService: PreviousRouteService) {
    this.userDetails = new UserModel();
    /*validation*/
    this.resetPasswordForm = this.formBuilder.group({
      usernumber: ['', Validators.required],
      password: ['', [Validators.required,
        Validators.minLength(6),
        Validators.maxLength(10)]],
      confirmpassword: ['', [Validators.required]],
    });
    /*validation end*/
  }

  /**
   * Component initialization method
   * 
   * Performs the following operations:
   * 1. Initiates guest login session for the password reset flow
   * 2. Tracks the current route in navigation history
   * 
   * @async
   * @method ngOnInit
   * @returns {Promise<void>}
   */
  public async ngOnInit() {
    this.login();
    this.previousRouteService.setPreviousRoute(this.locationStrategy.path());
  }

  /**
   * Initializes guest session for password reset flow
   * 
   * Authenticates as a guest user with hardcoded credentials to allow
   * unauthenticated users to access the password reset functionality.
   * On successful login, stores user data in localStorage.
   * On failure, redirects to home page.
   * 
   * @async
   * @method login
   * @returns {Promise<void>}
   * @throws {Error} If session login fails
   */
  async login() {
    try {
      this.sessionModel = await this.sessionService.sessionLogin('guest'.toLowerCase(), '$2a$10$sl0Lf6zgnyZ9cS/MKidFgOLbmOpOhtjZgf/OWxTCQRXku9qaxNBTS', 'GUEST');
      if (this.sessionModel) {
        localStorage.setItem('aquLoginData', JSON.stringify(this.sessionModel.user));
        this.submitted = false;
      } else {
        this.router.navigate(['/']);
      }
    } catch (error) {
      alert(error);
    }
  }

  /**
   * Getter method to access form controls
   * 
   * Provides convenient access to form controls in templates using the syntax f.controlName
   * 
   * @method f
   * @returns {AbstractControl} - The form controls object
   * 
   * @example
   * // In template: f.usernumber?.invalid && (f.usernumber?.dirty || f.usernumber?.touched)
   */
  public get f() {
    return this.resetPasswordForm.controls;
  }

  /**
   * Creates a new password for the user after validation
   * 
   * This method performs the following validations and operations:
   * 1. Marks form as submitted for error display
   * 2. Validates form structure and password length
   * 3. Confirms password matches confirm password field
   * 4. Retrieves user details by mobile number
   * 5. Validates user has a dialing code (address/location information)
   * 6. Validates user's person type is one of allowed types:
   *    - Business Admin, Farmer, Farm Manager, Provider, Provider Manager
   * 7. Updates password in the database
   * 8. Clears localStorage and navigates to login on success
   * 
   * Error scenarios:
   * - Form validation fails - returns without action
   * - Password confirmation mismatch - shows alert
   * - User not found - no action (caught in validation)
   * - Missing address/dialing code - shows alert and returns
   * - Invalid person type - shows alert and returns
   * - Password update fails - shows error alert
   * 
   * @async
   * @method createPassword
   * @returns {Promise<boolean|void>}
   * @throws {Error} If database operations fail
   * 
   * @example
   * await this.createPassword(); // Called on form submission
   */
  public async createPassword() {
    try {
      this.submitted = true;
      // stop here if form is invalid
      if (this.resetPasswordForm.invalid) {
        return;
      }
      if (this.userDetails.password != this.userDetails.confirm_password) {
        alert('Incorrect confirm password');
        return false;
      }
      let userDetails = await this.userService.getUserDetailsByNumber(this.userDetails.userName);
      if (userDetails) {
        if (userDetails.Persons.length > 0 && [undefined, null, 'undefined', 'N.A.'].includes(userDetails.Persons[0]['dialingCode'])) {
          alert('Unable to reset the password due to address not found for the provided mobile number please contact Customer support');
          return false;
        } else if(userDetails.Persons.length > 0 && !['Business Admin', 'Farmer', 'Farm Manager', 'Provider', 'Provider Manager'].includes(userDetails.Persons[0]['personType'][0]['name'])) {
          alert('Unable to reset the password because provided mobile number is not an Farm or Provider business please contact Customer support');
          return false;
        } else {
          let passwordChecking = await this.userService.updatePassword(this.userDetails.password, this.userDetails.userName);
          if (passwordChecking) {
            this.submitted = false;
            alert('Password reset successfully');
            localStorage.clear();
            this.router.navigate(['/login']);
          }
        }
      }
    } catch (error) {
      alert(error);
    }
  }
}
